import { isAxiosError } from 'axios'
import { defineStore } from 'pinia'

import {
  type CreateWorkerPayload,
  type CreateWorkerVersionPayload,
  type ImportWorkerVersionPayload,
  type ListWorkersParameters,
  type PageNumberPaginationParameters,
  type UpdateWorkerPayload,
  type WorkerVersionListParameters,
  createUserWorkerRun,
  createWorker,
  createWorkerVersion,
  importWorkerVersion,
  listUserWorkerRuns,
  listWorkerTypes,
  listWorkerVersions,
  listWorkers,
  retrieveFeatureWorkerVersion,
  retrieveRecommendedWorkerVersion,
  retrieveWorker,
  retrieveWorkerRun,
  retrieveWorkerVersion,
  updateWorker,
} from '@/api'
import { errorParser } from '@/helpers'
import type { ArkindexFeature, UUID } from '@/types'
import type { WorkerRun } from '@/types/process'
import type { Worker, WorkerType, WorkerVersion } from '@/types/worker'

import { useNotificationStore } from './notification'
import { useWorkerConfigurationStore } from './workerConfiguration'

interface State {
  workers: {
    [workerId: UUID]: Worker
  }
  workerTypes: {
    [typeId: UUID]: WorkerType
  }
  workerVersions: {
    [workerVersionId: UUID]: WorkerVersion
  }
  recommendedWorkerVersions: {
    [workerId: UUID]: WorkerVersion | null
  }
  featureWorkerVersionIds: {
    [feature in ArkindexFeature]?: UUID
  }
  workerRuns: {
    [workerRunId: UUID]: WorkerRun
  }
}

export const useWorkerStore = defineStore('worker', {
  state: (): State => ({
    workers: {},
    workerTypes: {},
    workerVersions: {},
    recommendedWorkerVersions: {},
    featureWorkerVersionIds: {},
    workerRuns: {},
  }),
  actions: {
    async listWorkers(params: ListWorkersParameters = {}) {
      const resp = await listWorkers(params)
      this.workers = {
        ...this.workers,
        ...Object.fromEntries(resp.results.map((worker) => [worker.id, worker])),
      }
      return resp
    },

    async createWorker(params: CreateWorkerPayload) {
      const resp = await createWorker(params)
      this.workers[resp.id] = resp
      // No need to fetch the worker type, as local workers cannot be used to build a worker process
      return resp
    },

    async updateWorker(id: UUID, params: UpdateWorkerPayload) {
      const resp = await updateWorker(id, params)
      this.workers[id] = resp
      return resp
    },

    async listWorkerTypes(page = 1) {
      if (page === 1) this.workerTypes = {}
      try {
        const resp = await listWorkerTypes({ page })
        this.workerTypes = {
          ...this.workerTypes,
          ...Object.fromEntries(resp.results.map((type) => [type.id, type])),
        }
        if (!resp || !resp.number || page !== resp.number) {
          // Avoid any loop
          throw new Error('Pagination failed listing worker types')
        }
        // Load other pages
        if (resp.next) this.listWorkerTypes(page + 1)
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 403) {
          throw err
        } else {
          useNotificationStore().notify({ type: 'error', text: errorParser(err) })
        }
      }
    },

    async listVersions(workerId: UUID, params: WorkerVersionListParameters) {
      const resp = await listWorkerVersions(workerId, params)
      this.workerVersions = {
        ...this.workerVersions,
        ...Object.fromEntries(resp.results.map((v) => [v.id, v])),
      }
      this.workers = {
        ...this.workers,
        ...Object.fromEntries(
          resp.results.map((v) => [
            v.worker.id,
            { ...(this.workers[v.worker.id] ?? {}), ...v.worker },
          ]),
        ),
      }
      return resp
    },

    async createWorkerVersion(workerId: UUID, params: CreateWorkerVersionPayload) {
      const resp = await createWorkerVersion(workerId, params)
      this.workerVersions[resp.id] = resp
      return resp
    },

    async importWorkerVersion(workerId: UUID, params: ImportWorkerVersionPayload) {
      const response = await importWorkerVersion(workerId, params)
      this.workerVersions[response.id] = response
      return response
    },

    async getWorkerVersion(workerVersionId: UUID) {
      this.workerVersions[workerVersionId] = await retrieveWorkerVersion(workerVersionId)
    },

    async getRecommendedWorkerVersion(workerId: UUID) {
      try {
        const resp = await retrieveRecommendedWorkerVersion(workerId)
        this.recommendedWorkerVersions[workerId] = resp
        return resp
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          this.recommendedWorkerVersions[workerId] = null
          return null
        } else throw err
      }
    },

    async getFeatureWorkerVersion(feature: ArkindexFeature) {
      try {
        const workerVersion = await retrieveFeatureWorkerVersion(feature)
        this.workerVersions[workerVersion.id] = workerVersion
        this.featureWorkerVersionIds[feature] = workerVersion.id
      } catch (err) {
        useNotificationStore().notify({ type: 'error', text: errorParser(err) })
      }
    },

    async getWorker(workerId: UUID) {
      this.workers[workerId] = await retrieveWorker(workerId)
    },

    async getWorkerRun(workerRunId: UUID) {
      const data = await retrieveWorkerRun(workerRunId)
      this.workerRuns[workerRunId] = data
      // Store the configuration in the workerConfigurations store if it exists
      if (!data.configuration) return
      const workerConfigurationStore = useWorkerConfigurationStore()
      if (!(data.worker_version.worker.id in workerConfigurationStore.configurations))
        workerConfigurationStore.configurations[data.worker_version.worker.id] = {}
      workerConfigurationStore.configurations[data.worker_version.worker.id][
        data.configuration.id
      ] = data.configuration
    },

    async listUserWorkerRuns(params: PageNumberPaginationParameters = {}) {
      const resp = await listUserWorkerRuns(params)
      this.workerRuns = {
        ...this.workerRuns,
        ...Object.fromEntries(resp.results.map((run) => [run.id, run])),
      }
      return resp
    },

    async createUserWorkerRun(versionId: UUID) {
      const resp = await createUserWorkerRun(versionId)
      this.workerRuns[resp.id] = resp
      return resp
    },
  },
})
